import React from 'react';
import { Stack, Typography } from '@mui/material';

import { Loader } from 'components/Loader';
import { useSent } from '../api/getSent';
import { formatDate } from '../utils/formatDate';
import { PagedList, usePaging } from './PagedList';
import { QnaCard, QnaEmpty, QnaEntry, visibilityLabels } from './QnaCard';
import { QnaFiles } from './QnaFiles';

const statusLabels = {
  waiting: { label: 'Waiting', color: 'warning' },
  answered: { label: 'Answered', color: 'primary' },
  declined: { label: 'Declined', color: 'error' },
};

const SentQuestion = ({ resource, id, question }) => {
  const status = statusLabels[question.status] ?? statusLabels.waiting;

  return (
    <QnaCard
      title={question.number ? `Question ${question.number}` : 'Question'}
      label={status.label}
      labelColor={status.color}
    >
      <QnaEntry
        heading={`Question submitted ${formatDate(question.created_on)}`}
        author={question.asked_by_name}
        text={question.question}
      />
      {question.status === 'answered' && question.revised_question && (
        <QnaEntry heading="Revised question" text={question.revised_question} />
      )}
      <QnaFiles
        resource={resource}
        id={id}
        files={question.question_files}
        heading="Question attachments"
      />
      {question.status === 'answered' && (
        <>
          <QnaEntry
            heading={`Answer submitted ${formatDate(question.answered_on)}`}
            text={question.answer}
          />
          <QnaFiles
            resource={resource}
            id={id}
            files={question.answer_files}
            heading="Answer attachments"
          />
          <Typography variant="caption" color="text.secondary">
            Published as {visibilityLabels[question.visibility]?.toLowerCase()}
            {question.visibility === 'limited'
              ? ', visible only to your organization'
              : ', visible to all suppliers'}
          </Typography>
        </>
      )}
      {question.status === 'declined' && (
        <QnaEntry
          heading={`Declined ${formatDate(question.declined_on)}`}
          text={question.decline_reason}
        />
      )}
    </QnaCard>
  );
};

export const SentList = ({ resource, id }) => {
  const paging = usePaging();
  const { data, isLoading, isError } = useSent(resource, id, paging);

  if (isLoading) return <Loader />;
  if (isError)
    return <QnaEmpty title="Something went wrong" message="Your questions could not be loaded" />;
  if (!data?.total) {
    return (
      <QnaEmpty
        title="No questions sent"
        message="Questions asked by your organization will show here"
      />
    );
  }

  return (
    <PagedList paging={paging} total={data.total} sortLabel="Sorted by date asked">
      <Stack gap={2}>
        {data.items.map((question) => (
          <SentQuestion
            key={question.question_id}
            resource={resource}
            id={id}
            question={question}
          />
        ))}
      </Stack>
    </PagedList>
  );
};
